import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { isEqual } from 'lodash'
import { AlertCircle, Check, ChevronsUpDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import JourneyAnalytics from '../journey-analytics/page'
import MigrationsList from '../migrations/page'
import PartnerList from '../partner/page'
import { PricingTierSettings } from '../pricing-tier/columns'
import RoleList from '../role/page'
import UsageMetrics from '../usage-metrics/page'
import UserList from '../user/page'

import { OrgActivityTab } from './activity/org-activity-tab'
import { ORG_ACTIVITY_PARAMS } from './activity/use-activity-params'

import { PricingTierComponents } from '@/api'
import { OrganizationCancelDeletionDialog } from '@/components/organization-cancel-deletion-dialog'
import OrganizationDataPoints from '@/components/organization-datapoints'
import { OrganizationDeleteDialog } from '@/components/organization-delete-dialog'
import OrganizationFeatureSettings from '@/components/organization-feature-settings'
import OrganizationHubspotCompany from '@/components/organization-hubspot-company'
import OrganizationHubspotData from '@/components/organization-hubspot-data'
import OrganizationPartnership from '@/components/organization-partnership'
import OrganizationSnapshotSchedule from '@/components/organization-snapshot-schedule'
import PricingTierAdvancedSettings from '@/components/pricing-tier-settings'
import { SecureProxyWhitelistDialog } from '@/components/secure-proxy-whitelist-dialog'
import { SegmentsInput } from '@/components/segments-input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelectAutoComplete } from '@/components/ui/multi-select-autocomplete'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/use-toast'
import { IOrganization } from '@/models'
import { useAuth, useDataPoints, usePricingTiers, useTags } from '@/providers'
import { useOrganizationCleanup } from '@/providers/OrganizationCleanupContextProvider'
import {
  ORGANIZATION_QUERY_IDS,
  useMutationAssignPricingTier,
  useMutationUpdateOrganization,
  useMutationUpdateOrganizationFeatures,
  useMutationUpdatePricingTier,
  useMutationUpdateRole,
  notifyOrganizationSettingUpdate,
  useQueryGetActivePartnerByOrgId,
  useQueryGetAllRoles,
  useQueryGetOrgElasticCluster,
  useQueryGetOrganization,
  useQueryGetOrganizationFeatureSettings,
  useQueryGetOrganizationSettings,
  useQueryGetPricingTierByOrganizationId,
  useQueryUsersByOrgId,
  useQueryGetOrganizations
} from '@/services'
import { getGrantsForPricingTier } from '@/utils'
import { Permissions, isPermitted } from '@/utils/permission-manager'
import { withChangedSegments } from '@/utils/segments'

const ORGANIZATION_TABS = [
  'settings',
  'users',
  'activity',
  'roles',
  'features',
  'migrations',
  'partners',
  'usage-metrics',
  'journey_analytics',
  'hubspot-data'
] as const

type OrganizationTab = (typeof ORGANIZATION_TABS)[number]

const DEFAULT_TAB: OrganizationTab = 'settings'

const OrganizationDetail = () => {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    organizationsToCleanup,
    isLoading: isLoadingOrganizationCleanupStatus
  } = useOrganizationCleanup()

  const { orgId } = useParams<{
    orgId: string
  }>()

  const tabParam = searchParams.get('tab') as OrganizationTab | null
  const activeTab =
    tabParam && ORGANIZATION_TABS.includes(tabParam) ? tabParam : DEFAULT_TAB

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      if (value === DEFAULT_TAB) next.delete('tab')
      else next.set('tab', value)

      // the Metrics tab's own state (period, drawer) doesn't apply to other tabs
      if (value !== 'activity') {
        ORG_ACTIVITY_PARAMS.forEach((param) => next.delete(param))
      }

      return next
    })
  }

  const { data: organization, isLoading: isOrganizationLoading } =
    useQueryGetOrganization(
      {
        orgId: orgId!
      },
      {
        enabled: Boolean(orgId)
      }
    )

  const {
    data: organizationSettings,
    isLoading: isLoadingOrganizationSettings
  } = useQueryGetOrganizationSettings(orgId!, {
    enabled: Boolean(orgId)
  })

  const {
    data: featureSettings,
    isLoading: isLoadingFeatureSettings,
    error: featureSettingsError
  } = useQueryGetOrganizationFeatureSettings(orgId!, {
    enabled: Boolean(orgId)
  })

  const {
    organizationTags: allTags,
    isOrganizationTagsLoading: isAllTagsLoading
  } = useTags()

  const {
    isLoading: isLoadingPricingTiers,
    pricingTierAssignments,
    pricingTiers
  } = usePricingTiers()

  const { userData } = useAuth()

  const { dataPoints, isLoading: isDataPointsLoading } = useDataPoints()

  const [pricingTierState, setPricingTierState] =
    useState<PricingTierComponents.Schemas.PricingTier>()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showCancelDeletionDialog, setShowCancelDeletionDialog] =
    useState(false)
  const [showSecureProxyWhitelistDialog, setShowSecureProxyWhitelistDialog] =
    useState(false)

  const [selectedTags, setSelectedTags] = useState<string[]>(
    organization?.tags ?? []
  )
  const [selectedSegments, setSelectedSegments] = useState<string[]>(
    organization?.segments ?? []
  )
  const [initialSegments, setInitialSegments] = useState<string[]>(
    organization?.segments ?? []
  )
  const [selectedPricingTierId, setSelectedPricingTierId] = useState<
    string | undefined
  >()
  const [initialPricingTier, setInitialPricingTier] = useState<
    string | undefined
  >()

  const [organizationState, setOrganizationState] = useState<IOrganization>(
    {} as IOrganization
  )

  const [isUpdatingOrganization, setIsUpdatingOrganization] = useState(false)
  const [isUpdatingOrganizationFeatures, setIsUpdatingOrganizationFeatures] =
    useState(false)

  const [openParentOrgPopover, setOpenParentOrgPopover] = useState(false)

  const dataPoint = dataPoints.find((x) => x.org_id.toString() === orgId)

  useEffect(() => {
    if (organization) {
      setSelectedTags(organization?.tags ?? [])
      setSelectedSegments(organization.segments ?? [])
      setInitialSegments(organization.segments ?? [])
      setOrganizationState(organization)
    }
  }, [organization])

  const { data: assignedPricingTier, isLoading: isLoadingAssignedPricingTier } =
    useQueryGetPricingTierByOrganizationId(selectedPricingTierId!, orgId!, {
      enabled: Boolean(selectedPricingTierId) && Boolean(orgId)
    })

  useEffect(() => {
    if (assignedPricingTier) {
      handleResetPricingTierState(assignedPricingTier)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedPricingTier])

  const handleResetPricingTierState = (
    pricingTier: PricingTierComponents.Schemas.PricingTier
  ) => {
    const pricingTierDefaultValues = Object.fromEntries(
      PricingTierSettings.map((field) => [
        field.key,
        pricingTier?.settings?.[field.key] ?? field.default
      ]).filter((x) => x[0] !== 'name')
    )

    if (pricingTier?.id !== initialPricingTier) {
      pricingTier.override_settings = {}
    }
    setPricingTierState({
      ...pricingTier,
      settings: pricingTierDefaultValues
    })
  }

  useEffect(() => {
    if (pricingTierAssignments) {
      const pricingTierId = pricingTierAssignments.find(
        (x) => x.organization_id === orgId
      )?.pricing_tier_id

      if (!initialPricingTier) {
        setInitialPricingTier(pricingTierId)
      }
      setSelectedPricingTierId(pricingTierId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, pricingTierAssignments])

  const getMaxBillableUsersLastMonth = () => {
    return (
      (dataPoint?.max_users_last_month ?? 0) -
      (dataPoint?.non_billable_users_last_month ?? 0)
    )
  }

  const getAdditionalPaidUsersLastMonth = () => {
    // if pricing tier setting is overriden use it, otherwise use default pricing tier setting
    const freeUserLimit = pricingTierState?.override_settings?.free_user
      ? pricingTierState?.override_settings?.free_user?.quota
      : pricingTierState?.settings?.free_user?.quota || 0

    // if there's a freeUserLimit set and they have more users last month than freeUserLimit
    if (+freeUserLimit > 0 && getMaxBillableUsersLastMonth() > +freeUserLimit) {
      return getMaxBillableUsersLastMonth() - +freeUserLimit
    }

    return 0
  }

  const { data: activePartners, isLoading: isActivePartnersLoading } =
    useQueryGetActivePartnerByOrgId(orgId!, {
      enabled: Boolean(orgId)
    })

  const { data: endCustomerPortalUsers } = useQueryUsersByOrgId(
    orgId!,
    'END_CUSTOMER_PORTAL',
    {
      enabled: Boolean(orgId)
    }
  )

  const { data: installerPortalUsers } = useQueryUsersByOrgId(
    orgId!,
    'INSTALLER_PORTAL',
    {
      enabled: Boolean(orgId)
    }
  )

  const getActualPartnerUsers = () => {
    let actualPartnerUsers = 0

    if (!isDataPointsLoading && !isActivePartnersLoading) {
      for (const activePartner of activePartners?.data.results ?? []) {
        actualPartnerUsers +=
          dataPoints?.find(
            (dp) => dp.org_id.toString() === activePartner?.partner_org_id
          )?.actual_users ?? 0
      }
    }

    return actualPartnerUsers
  }

  const getMaxPartnerUsersLastMonth = () => {
    let maxPartnerUsers = 0

    if (!isDataPointsLoading && !isActivePartnersLoading) {
      for (const activePartner of activePartners?.data.results ?? []) {
        maxPartnerUsers +=
          dataPoints?.find(
            (dp) => dp.org_id.toString() === activePartner?.partner_org_id
          )?.max_users_last_month ?? 0
      }
    }

    return maxPartnerUsers
  }

  const { data: organizationRoles } = useQueryGetAllRoles(
    { orgId: orgId! },
    {
      enabled: Boolean(orgId)
    }
  )

  const rootRole = organizationRoles?.data.roles?.find((x) => x.slug === 'root')

  const elasticClusterForOrg = useQueryGetOrgElasticCluster(orgId!).data?.data
    ?.cluster

  const updateOrganization = useMutationUpdateOrganization()
  const updateOrganizationFeatures = useMutationUpdateOrganizationFeatures(
    orgId!
  )
  const updateOrganizationPricingTier = useMutationUpdatePricingTier(orgId)
  const assignPricingTier = useMutationAssignPricingTier()
  const updateRole = useMutationUpdateRole(rootRole?.id ?? '', orgId!)

  const handleUpdateOrganization = () => {
    setIsUpdatingOrganization(true)
    if (pricingTierState?.id) {
      assignPricingTier.mutate(
        {
          organization_id: orgId,
          pricing_tier_id: pricingTierState?.id
        },
        {
          onError: (err) => {
            toast({
              title: 'Error',
              description: `Error while assigning pricing tier. ${err?.['message']}`
            })
          }
        }
      )

      updateOrganizationPricingTier.mutate(
        { ...pricingTierState, updated_by: userData?.attributes?.email || '' },
        {
          onError: (err) => {
            toast({
              title: 'Error',
              description: `Error while updating pricing tier. ${err?.['message']}`
            })
          }
        }
      )
    }

    /**
     * This is an best effort attempt at checking if the organization is a Partner, as we
     * don't store any organization metadata that makes this distinction available.
     */
    const isPartnerOrganization =
      assignedPricingTier?.id === '23' ||
      assignedPricingTier?.name?.toLowerCase() === 'partner'

    const pricingTierGrants = getGrantsForPricingTier(
      pricingTierState!,
      isPartnerOrganization
    )
    const rootRoleGrants = (rootRole?.grants || []).filter((grant) => {
      return (
        !pricingTierState?.settings?.[grant.action.split(':')[0]] &&
        !pricingTierState?.override_settings?.[grant.action.split(':')[0]] &&
        !pricingTierGrants.find((x) => x.action === grant.action)
      )
    })

    const grants = [...pricingTierGrants, ...rootRoleGrants]

    updateRole.mutate({
      grants: grants,
      name: rootRole!.name,
      slug: rootRole!.slug!,
      id: rootRole!.id!,
      organization_id: rootRole!.organization_id!,
      type: rootRole!.type!,
      pricing_tier: assignedPricingTier?.name
    })

    const organizationUpdate = withChangedSegments(
      {
        ...organizationState,
        tags: selectedTags,
        company_size: organizationState.company_size,
        hubspot_company_id:
          organizationState.hubspot_company_id ||
          (null as unknown as undefined),
        pricing_tier_id: pricingTierState?.id
      },
      selectedSegments,
      initialSegments
    )

    updateOrganization.mutate(organizationUpdate, {
      onSuccess: () => {
        setInitialPricingTier(pricingTierState?.id)
        setInitialSegments(selectedSegments)
        setIsUpdatingOrganization(false)
        toast({
          title: 'Success',
          description: 'Organization updated successfully'
        })
      },
      onError: (err) => {
        setIsUpdatingOrganization(false)
        toast({
          title: 'Error',
          description: `Error while updating organization. ${err?.['message']}`
        })
      }
    })
  }

  const isDisabledToUpdate = Boolean(
    organizationsToCleanup?.some((x) => x.org_id === orgId) ||
      isLoadingOrganizationCleanupStatus
  )

  const { data: productionOrgs } = useQueryGetOrganizations()

  return (
    <Tabs className="w-full" onValueChange={handleTabChange} value={activeTab}>
      <TabsList className="grid w-full grid-cols-10">
        <TabsTrigger value="settings">Settings</TabsTrigger>
        <TabsTrigger value="users">Users</TabsTrigger>
        <TabsTrigger value="activity">Metrics</TabsTrigger>
        <TabsTrigger value="roles">Roles</TabsTrigger>
        <TabsTrigger value="features">Features</TabsTrigger>
        <TabsTrigger value="migrations">Migrations</TabsTrigger>
        <TabsTrigger value="partners">Partners</TabsTrigger>
        <TabsTrigger value="usage-metrics">Power BI</TabsTrigger>
        <TabsTrigger value="journey_analytics">Journey Analytics</TabsTrigger>
        <TabsTrigger value="hubspot-data">HubSpot Data</TabsTrigger>
      </TabsList>
      {isDisabledToUpdate && !isLoadingOrganizationCleanupStatus && (
        <Alert className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              This organization is marked for cleanup. You can not update any
              data on this organization.
            </span>
            <Button
              className="ml-4 shrink-0"
              disabled={!isPermitted(Permissions.DELETE_ORG, userData)}
              onClick={() => setShowCancelDeletionDialog(true)}
              size="sm"
              variant="outline"
            >
              Cancel Deletion
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <TabsContent value="settings">
        <div className="space-y-4">
          <div className="flex space-x-12">
            <div className="w-1/2 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="id">ID</Label>
                <Input disabled id="id" value={organization?.id} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  disabled={isDisabledToUpdate}
                  id="name"
                  onChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      name: e.target.value
                    }))
                  }
                  value={organizationState?.name ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label>Production vs. Sandbox</Label>
                <Select
                  disabled={isDisabledToUpdate}
                  onValueChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      organization_use: e as 'Production' | 'Sandbox',
                      parent_production_org_id:
                        e === 'Production'
                          ? undefined
                          : prev.parent_production_org_id
                    }))
                  }
                  value={organizationState.organization_use}
                >
                  <SelectTrigger
                    className={classNames({
                      'text-primary': organizationState.organization_use,
                      'text-placeholder': !organizationState.organization_use
                    })}
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Production'}>Production</SelectItem>
                    <SelectItem value={'Sandbox'}>Sandbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {organizationState.organization_use === 'Sandbox' && (
                <div className="space-y-2">
                  <Label>Parent Production Organization</Label>
                  <Popover
                    onOpenChange={setOpenParentOrgPopover}
                    open={openParentOrgPopover}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        aria-expanded={openParentOrgPopover}
                        className={classNames('w-full justify-between', {
                          'text-primary':
                            organizationState.parent_production_org_id,
                          'text-placeholder':
                            !organizationState.parent_production_org_id
                        })}
                        disabled={isDisabledToUpdate}
                        role="combobox"
                        variant="outline"
                      >
                        {organizationState.parent_production_org_id
                          ? productionOrgs?.find(
                              (org) =>
                                org.id ===
                                organizationState.parent_production_org_id
                            )?.name
                          : 'Select parent production organization'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-[var(--radix-popover-trigger-width)] p-0"
                    >
                      <Command>
                        <CommandInput
                          className="h-9"
                          placeholder="Search organizations..."
                        />
                        <CommandList>
                          <CommandEmpty>No organization found.</CommandEmpty>
                          <CommandGroup>
                            {productionOrgs
                              ?.filter(
                                (org) => org.organization_use !== 'Sandbox'
                              )
                              ?.map((org) => (
                                <CommandItem
                                  className="flex items-center justify-between py-2 px-4"
                                  key={org.id}
                                  onSelect={() => {
                                    setOrganizationState((prev) => ({
                                      ...prev,
                                      parent_production_org_id: org?.id
                                    }))
                                    setOpenParentOrgPopover(false)
                                  }}
                                  value={`${org?.name ?? ''} ${org?.id ?? ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Check
                                      className={classNames(
                                        'mr-2 h-4 w-4',
                                        organizationState.parent_production_org_id ===
                                          org?.id
                                          ? 'opacity-100'
                                          : 'opacity-0'
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {org?.name ?? ''}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        ID: {org?.id ?? ''}
                                      </span>
                                    </div>
                                  </div>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  disabled={isDisabledToUpdate}
                  id="name"
                  onChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      email: e.target.value
                    }))
                  }
                  type="email"
                  value={organizationState?.email ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_number">Customer Number</Label>
                <Input
                  disabled={isDisabledToUpdate}
                  id="customer_number"
                  onChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      customer_number: e.target.value
                    }))
                  }
                  value={organizationState?.customer_number ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="symbol">Symbol</Label>
                <Input
                  disabled
                  id="symbol"
                  value={organization?.symbol ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Input disabled id="type" value={organization?.type} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Input
                  disabled
                  id="status"
                  value={organization?.status ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label>Severity Group</Label>
                <Select
                  disabled={isDisabledToUpdate}
                  onValueChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      severity_group: e
                    }))
                  }
                  value={organizationState.severity_group}
                >
                  <SelectTrigger
                    className={classNames({
                      'text-primary': organizationState.severity_group,
                      'text-placeholder': !organizationState.severity_group
                    })}
                  >
                    <SelectValue placeholder="Select a Severity Group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Low'}>Low</SelectItem>
                    <SelectItem value={'Medium'}>Medium</SelectItem>
                    <SelectItem value={'High'}>High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="created_date">Created At</Label>
                <Input
                  disabled
                  id="created_date"
                  value={
                    new Date(
                      organization?.created_date as string
                    )?.toLocaleString() ?? ''
                  }
                />
              </div>
              {!isDisabledToUpdate && (
                <div className="w-full flex justify-between">
                  <Button
                    loading={isUpdatingOrganization}
                    onClick={() => handleUpdateOrganization()}
                  >
                    Update Organization
                  </Button>

                  <Button
                    disabled={!isPermitted(Permissions.DELETE_ORG, userData)}
                    loading={isUpdatingOrganization}
                    onClick={() => setShowDeleteDialog(true)}
                    variant={'destructive'}
                  >
                    Delete Organization
                  </Button>
                </div>
              )}
            </div>
            <div className="w-1/2 space-y-4">
              <div className="space-y-2">
                <Label>Segments</Label>
                <SegmentsInput
                  disabled={isDisabledToUpdate}
                  onChange={setSelectedSegments}
                  value={selectedSegments}
                />
              </div>
              <div className="space-y-2">
                <Label>Tags</Label>
                <MultiSelectAutoComplete
                  allowNewValues
                  disabled={isDisabledToUpdate}
                  isLoading={isAllTagsLoading}
                  items={allTags || []}
                  onNewItem={(item) => item}
                  onSelect={(item) =>
                    setSelectedTags((prev) => [...prev, item as string])
                  }
                  onUnselect={(item) =>
                    setSelectedTags((prevItems) =>
                      prevItems.filter(
                        (selectedItem) => selectedItem !== (item as string)
                      )
                    )
                  }
                  placeholder="Choose the tags to assign to the organization"
                  selectedItems={selectedTags}
                />
              </div>
              <div className="space-y-2">
                <Label>Subscription Status</Label>
                <Select
                  disabled={isDisabledToUpdate}
                  onValueChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      subscription_status: e,
                      subscription_expiry_date:
                        e === 'Active' ? '' : prev.subscription_expiry_date
                    }))
                  }
                  value={organizationState.subscription_status}
                >
                  <SelectTrigger
                    className={classNames({
                      'text-primary': organizationState.subscription_status,
                      'text-placeholder': !organizationState.subscription_status
                    })}
                  >
                    <SelectValue placeholder="Select a subscription status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Active'}>Active</SelectItem>
                    <SelectItem value={'Churning'}>Churning</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {organizationState.subscription_status === 'Churning' && (
                <div className="space-y-2">
                  <Label>Subscription End Date</Label>
                  <Input
                    className="[&::-webkit-calendar-picker-indicator]:dark:invert"
                    disabled={isDisabledToUpdate}
                    onChange={(e) =>
                      setOrganizationState((prev) => ({
                        ...prev,
                        subscription_expiry_date: e.target.value
                      }))
                    }
                    placeholder="Select when the subscription will end"
                    type="date"
                    value={organizationState.subscription_expiry_date ?? ''}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Company Size</Label>
                <Select
                  disabled={isDisabledToUpdate}
                  onValueChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      company_size: e
                    }))
                  }
                  value={organizationState.company_size}
                >
                  <SelectTrigger
                    className={classNames({
                      'text-primary': organizationState.company_size,
                      'text-placeholder': !organizationState.company_size
                    })}
                  >
                    <SelectValue placeholder="Select a company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Small'}>Small</SelectItem>
                    <SelectItem value={'Medium'}>Medium</SelectItem>
                    <SelectItem value={'Large'}>Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pricing_tier">Pricing Tier</Label>
                <Select
                  disabled={isDisabledToUpdate}
                  onValueChange={(pricingTierId) => {
                    setSelectedPricingTierId(pricingTierId)
                  }}
                  value={selectedPricingTierId}
                >
                  <SelectTrigger
                    className={classNames({
                      'text-primary': selectedPricingTierId,
                      'text-placeholder': !selectedPricingTierId
                    })}
                  >
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>

                  <SelectContent>
                    {pricingTiers?.map((pricingTier) => {
                      return (
                        <SelectItem
                          key={pricingTier.id}
                          value={pricingTier.id!}
                        >
                          {pricingTier?.name}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="id">Elastic Search Cluster</Label>
                <Input
                  disabled
                  id="org_es_cluster"
                  value={elasticClusterForOrg ?? ''}
                />
              </div>
              <PricingTierAdvancedSettings
                defaultOpen={false}
                disabled={isDisabledToUpdate}
                isRenderedInPricingTierSettingsPage={false}
                loading={isLoadingAssignedPricingTier || isLoadingPricingTiers}
                onChange={(e, key) => {
                  setPricingTierState({
                    ...pricingTierState,
                    override_settings: {
                      ...pricingTierState?.override_settings,
                      [key]: e
                    }
                  })
                }}
                options={PricingTierSettings.filter(
                  (x) => x.key !== 'name'
                ).map((x) => ({
                  ...x,
                  value:
                    pricingTierState?.override_settings?.[x.key] ??
                    pricingTierState?.settings?.[x.key],
                  isShowGlobalSetting: pricingTierState?.override_settings?.[
                    x.key
                  ]
                    ? !isEqual(
                        pricingTierState?.settings?.[x.key],
                        pricingTierState?.override_settings?.[x.key]
                      )
                    : false,
                  globalSetting:
                    pricingTierState?.settings?.[x.key] ?? x.default
                }))}
              />
              <OrganizationDataPoints
                dataPoint={{
                  max_customer: dataPoint?.max_customer || 0,
                  actual_customer: dataPoint?.actual_customer || 0,
                  actual_users: dataPoint?.actual_users || 0,
                  max_users_last_month: dataPoint?.max_users_last_month || 0,
                  maxBillableUsersLastMonth: getMaxBillableUsersLastMonth(),
                  additionalPaidUsers: getAdditionalPaidUsersLastMonth()
                }}
                defaultOpen={false}
                loading={isDataPointsLoading || isOrganizationLoading}
              />
              <OrganizationPartnership
                defaultOpen={false}
                loading={isDataPointsLoading || isOrganizationLoading}
                partnershipData={{
                  actual_partner_users: getActualPartnerUsers() || 0,
                  max_partner_users: getMaxPartnerUsersLastMonth() || 0,
                  active_partnerships:
                    activePartners?.data?.results?.length || 0,
                  ecp_users: endCustomerPortalUsers?.data?.results?.length || 0,
                  ip_users: installerPortalUsers?.data?.results?.length || 0
                }}
              />
              <OrganizationHubspotCompany
                defaultOpen={false}
                disabled={isDisabledToUpdate}
                loading={isOrganizationLoading}
                onCompanySelect={(companyId) =>
                  setOrganizationState((prev) => ({
                    ...prev,
                    hubspot_company_id: companyId
                  }))
                }
                selectedCompanyId={organizationState.hubspot_company_id}
              />
              <Button
                className="w-full"
                onClick={() => setShowSecureProxyWhitelistDialog(true)}
                variant="outline"
              >
                Secure Proxy Whitelist
              </Button>
            </div>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="hubspot-data">
        <OrganizationHubspotData orgId={orgId} />
      </TabsContent>
      <TabsContent value="features">
        {featureSettingsError && (
          <p className="mb-4 text-sm text-destructive">
            Failed to load feature list: {featureSettingsError.message}
          </p>
        )}
        <OrganizationFeatureSettings
          defaultOpen={true}
          disabled={isDisabledToUpdate}
          featureFlags={featureSettings?.feature_flags ?? []}
          loading={
            isLoadingOrganizationSettings ||
            isLoadingFeatureSettings ||
            isUpdatingOrganizationFeatures
          }
          onChange={(setting: {
            key: string
            value: { [key: string]: boolean }
          }) => {
            setIsUpdatingOrganizationFeatures(true)
            updateOrganizationFeatures.mutate(setting, {
              onSuccess: () => {
                setIsUpdatingOrganizationFeatures(false)
                toast({
                  title: 'Success',
                  description: 'Setting updated successfully'
                })
                notifyOrganizationSettingUpdate({
                  orgId: orgId!,
                  key: setting.key,
                  value: setting.value,
                  userEmail:
                    userData?.attributes['email'] ??
                    JSON.stringify(userData?.attributes),
                  orgName: organization?.name ?? ''
                }).catch((error) => {
                  console.error(
                    'Failed to notify organization setting update',
                    error
                  )
                })
              },
              onError: (err) => {
                setIsUpdatingOrganizationFeatures(false)
                toast({
                  title: 'Error',
                  description: `Error while updating setting. ${err?.['message']}`
                })
              }
            })
          }}
          settings={organizationSettings?.data ?? {}}
        />
        <OrganizationSnapshotSchedule
          disabled={isDisabledToUpdate}
          orgId={orgId!}
        />
      </TabsContent>
      <TabsContent value="migrations">
        <MigrationsList
          disabled={isDisabledToUpdate}
          organizationId={organization?.id}
        />
      </TabsContent>
      <TabsContent value="users">
        <UserList
          loginAsDisabled={isDisabledToUpdate}
          organizationId={organization?.id}
        />
      </TabsContent>
      <TabsContent value="activity">
        {orgId && activeTab === 'activity' && (
          <OrgActivityTab orgId={orgId} orgName={organization?.name} />
        )}
      </TabsContent>
      <TabsContent value="roles">
        <RoleList
          disabled={isDisabledToUpdate}
          organizationId={organization?.id}
        />
      </TabsContent>
      <TabsContent value="partners">
        <PartnerList />
      </TabsContent>
      <TabsContent value="journey_analytics">
        {orgId && <JourneyAnalytics orgId={orgId} />}
      </TabsContent>
      <TabsContent value="usage-metrics">
        {orgId && <UsageMetrics orgId={orgId} />}
      </TabsContent>
      <OrganizationCancelDeletionDialog
        onCancelDeletion={() => {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATIONS_TO_CLEANUP]
          })
          updateOrganization.mutate(
            {
              id: orgId,
              tags: selectedTags.filter((tag) => tag !== 'Marked for Cleanup')
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATION, orgId]
                })
              }
            }
          )
        }}
        onOpenChange={setShowCancelDeletionDialog}
        open={showCancelDeletionDialog}
        organization={organization}
      />
      <OrganizationDeleteDialog
        onDelete={() => {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATIONS_TO_CLEANUP]
          })
          updateOrganization.mutate(
            {
              id: orgId,
              tags: [...selectedTags, 'Marked for Cleanup']
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATION, orgId]
                })
              }
            }
          )
        }}
        onOpenChange={setShowDeleteDialog}
        open={showDeleteDialog}
        organization={organization}
      />
      {organization?.id && showSecureProxyWhitelistDialog && (
        <SecureProxyWhitelistDialog
          onOpenChange={setShowSecureProxyWhitelistDialog}
          open={showSecureProxyWhitelistDialog}
          orgId={organization.id}
        />
      )}
    </Tabs>
  )
}

export default OrganizationDetail
